
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, BarChart3, MapPin, Calendar } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

interface MarketInsight {
  area: string;
  avgPrice: number;
  priceChange: number;
  demandLevel: 'high' | 'medium' | 'low';
  propertyCount: number;
}

interface PriceTrend {
  month: string;
  price: number;
}

const PropertyInsights = () => {
  const { t } = useLanguage();
  const [marketData, setMarketData] = useState<MarketInsight[]>([]);
  const [priceTrends, setPriceTrends] = useState<PriceTrend[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate fetching market data
    setTimeout(() => {
      setMarketData([
        {
          area: 'New Cairo',
          avgPrice: 4500,
          priceChange: 12.5,
          demandLevel: 'high',
          propertyCount: 245
        },
        {
          area: 'Maadi',
          avgPrice: 5200,
          priceChange: -3.2,
          demandLevel: 'medium',
          propertyCount: 189
        },
        {
          area: 'Heliopolis',
          avgPrice: 3800,
          priceChange: 8.7,
          demandLevel: 'high',
          propertyCount: 312
        },
        {
          area: 'Zamalek',
          avgPrice: 6800,
          priceChange: 5.1,
          demandLevel: 'low',
          propertyCount: 67
        }
      ]);

      setPriceTrends([
        { month: 'Jan', price: 4200 },
        { month: 'Feb', price: 4350 },
        { month: 'Mar', price: 4400 },
        { month: 'Apr', price: 4550 },
        { month: 'May', price: 4500 },
        { month: 'Jun', price: 4650 }
      ]);

      setLoading(false);
    }, 1000);
  }, []);

  const getDemandColor = (level: string) => {
    switch (level) {
      case 'high': return 'bg-green-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getDemandText = (level: string) => {
    switch (level) {
      case 'high': return t('high_demand', 'طلب عالي', 'High Demand');
      case 'medium': return t('medium_demand', 'طلب متوسط', 'Medium Demand');
      case 'low': return t('low_demand', 'طلب منخفض', 'Low Demand');
      default: return level;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map(i => (
          <Card key={i}>
            <CardContent className="py-8">
              <div className="h-64 bg-muted animate-pulse rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            {t('market_insights', 'رؤى السوق', 'Market Insights')}
          </CardTitle>
          <CardDescription>
            {t('housing_market_analysis', 'تحليل سوق الإسكان الطلابي', 'Student housing market analysis and trends')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4">
            {marketData.map((area, index) => (
              <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <h4 className="font-medium">{area.area}</h4>
                    <p className="text-sm text-muted-foreground">
                      {area.propertyCount} {t('properties', 'عقار', 'properties')}
                    </p>
                  </div>
                </div>
                <div className="text-right space-y-1">
                  <p className="font-semibold">{area.avgPrice} EGP</p>
                  <div className="flex items-center gap-2">
                    {area.priceChange > 0 ? (
                      <TrendingUp className="h-4 w-4 text-green-500" />
                    ) : (
                      <TrendingDown className="h-4 w-4 text-red-500" />
                    )}
                    <span className={`text-sm ${area.priceChange > 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {area.priceChange > 0 ? '+' : ''}{area.priceChange}%
                    </span>
                  </div>
                </div>
                <div className="ml-4">
                  <Badge className={getDemandColor(area.demandLevel)}>
                    {getDemandText(area.demandLevel)}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            {t('price_trends', 'اتجاهات الأسعار', 'Price Trends')}
          </CardTitle>
          <CardDescription>
            {t('avg_price_over_time', 'متوسط الأسعار عبر الوقت', 'Average rental prices over time')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={priceTrends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => [`${value} EGP`, 'Price']} />
                <Line type="monotone" dataKey="price" stroke="#8884d8" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('market_summary', 'ملخص السوق', 'Market Summary')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 border rounded-lg">
              <h4 className="text-2xl font-bold text-green-500">813</h4>
              <p className="text-sm text-muted-foreground">
                {t('total_properties', 'إجمالي العقارات', 'Total Properties')}
              </p>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <h4 className="text-2xl font-bold text-blue-500">4,685 EGP</h4>
              <p className="text-sm text-muted-foreground">
                {t('avg_price', 'متوسط السعر', 'Average Price')}
              </p>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <h4 className="text-2xl font-bold text-orange-500">+7.8%</h4>
              <p className="text-sm text-muted-foreground">
                {t('monthly_growth', 'النمو الشهري', 'Monthly Growth')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PropertyInsights;
